import { Export } from '@adgate/common/interfaces/export.interface';
import { Filter } from '@adgate/common/interfaces/filter.interface';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';
import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatRippleModule } from '@angular/material/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';
import { ProductionRequestFilter } from '@adgate/modules/production/types/production-request-filter.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ProductionService } from '@adgate/modules/production/services/production-service';
import { ProductionRequestVersionGridComponent } from '@adgate/modules/production/components/production-request-version-grid-component/production-request-version-grid-component';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { StateService } from '@adgate/common/services/state-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { Status } from '@adgate/common/types/status.type';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, finalize, switchMap } from 'rxjs';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { ProductionRequestFiltersDialogComponent } from '@adgate/modules/production/dialogs/production-request-filters-dialog-component/production-request-filters-dialog-component';
import { ProductionRequestEditDialogComponent } from '@adgate/modules/production/dialogs/production-request-edit-dialog-component/production-request-edit-dialog-component';
import { ProductionVersionEditDialogComponent } from '@adgate/modules/production/dialogs/production-version-edit-dialog-component/production-version-edit-dialog-component';
import { ProductionVersion } from '@adgate/modules/production/types/production-version.type';
import { LinkMasterCommercialDialogComponent } from '@adgate/modules/production/dialogs/link-master-commercial-dialog-component/link-master-commercial-dialog-component';
import * as XLSX from 'xlsx';
import { Commercial } from '@adgate/modules/tv/types/commecial.type';
import { ProductionRequestSampleDialogComponent } from '../../dialogs/production-request-sample-dialog-component/production-request-sample-dialog-component';

@Component({
  selector: 'production-request-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    ClipboardModule,
    MatRippleModule,
    MatTooltip,
    ObjectDescPipe,
    ProductionRequestVersionGridComponent
  ],
  providers: [DatePipe],
  templateUrl: './production-request-grid-component.html',
  styleUrl: './production-request-grid-component.scss',
})
export class ProductionRequestGridComponent implements OnInit, AfterViewInit, QuickSearch, Export, Filter
{
  @Input() description: string = "Blank";

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('versionGrid') versionGrid?: ProductionRequestVersionGridComponent;

  private dialogService: DialogService = inject(DialogService);
  private productionService: ProductionService = inject(ProductionService);
  private authService: AuthenticationService = inject(AuthenticationService);
  private stateService: StateService = inject(StateService);
  private datePipe: DatePipe = inject(DatePipe);

  public _snackBar: MatSnackBar = inject(MatSnackBar);

  displayedColumns: string[] = ['requestId', 'client', 'name', 'sampleVo', 'airDate', 'masterRefCode', 'masterBrand', 'status', 'actions'];
  dataSource: MatTableDataSource<ProductionRequest>;
  filter: ProductionRequestFilter;
  expandedProductionRequest: ProductionRequest | null = null;

  private readonly FILTER_STORAGE_KEY = 'adgate.production.requests.filter';

  allowEditHistory: boolean = false;
  allowEditRequest: boolean = false;

  constructor()
  {
    this.dataSource = new MatTableDataSource<ProductionRequest>([]);
    this.filter = new ProductionRequestFilter();
    this.initPrivileges();
    // this.filter.status = [{ id: 20, description: 'Open' }];
  }

  ngOnInit(): void
  {
    // Load persistent filter if one exists in storage
    const savedFilter = this.stateService.loadFilter(this.FILTER_STORAGE_KEY, ProductionRequestFilter);
    if (savedFilter) {
      this.filter = savedFilter;
    }

    this.onRefresh();
  }

  ngAfterViewInit(): void 
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onRefresh()
  {
    this.dialogService.showProgress();
    this.productionService.getProductionRequests(this.filter).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });    
  }

  onQuickSearch(filter: string | null): void 
  {
    this.dataSource.filter = filter!;
  }

  getQuickSearchPlaceholder(): string 
  {
    return "Search Requests";
  }

  onExport(): void 
  {
    if (this.dataSource.data.length > 0) {
      this.dialogService.showProgress();
      const jsonData = this.dataSource.data.map( (pr:ProductionRequest) => ({
        'Request Id': this.formatRequestId(pr.requestId),
        'Client': pr.client,
        'Name': pr.name,
        'Origin': pr.origin,
        'Req. Sample': pr.requireSample ? 'Yes' : 'No',
        'Req. VO': pr.requireVoiceover ? 'Yes' : 'No',
        'Air Date': this.datePipe.transform(pr.airDate,'d/M/yyyy'),
        'Master Ref. Code': pr.masterCommercialRefCode,
        'Master Brand': pr.masterCommercialBrand,
        'Master Product': pr.masterCommercialProduct,
        'Created': this.datePipe.transform(pr.dateCreated,'d/M/yyyy'),
        'Status': pr.status.description
      }));

      let filename = "production-request-export.xlsx";
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Requests');
      XLSX.writeFile(wb, filename);
      this.dialogService.hideProgress();
    } else {
      this.dialogService.openErrorDialog("There are no production requests to export");
    }
  }

  showFilters(): boolean 
  {
    return true;
  }

  onFilter(): void
  {
    const dialogRef = this.dialogService.openAnyDialog(ProductionRequestFiltersDialogComponent, {
      data: this.filter,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if ( result ) {
        this.filter = result;
        this.stateService.saveFilter(this.FILTER_STORAGE_KEY, this.filter);
        this.onRefresh();
      }
    });
  }  

  onNewRequest(): void
  {
    this.onEditRequest(new ProductionRequest());
  }

  onEditRequest(request: ProductionRequest): void
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(ProductionRequestEditDialogComponent, {
      data: request,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: ProductionRequest | null) => (result !== null) ),
      switchMap((result:ProductionRequest) => {
        this.dialogService.showProgress();
        return this.productionService.updateProductionRequests(result);
      }),
      switchMap((r:RESTApplicationResponse) => {
        successMessage = r.message;
        return this.productionService.getProductionRequests(this.filter);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => { 
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onAddVersion(request: ProductionRequest): void
  {
    const dialogRef = this.dialogService.openAnyDialog(ProductionVersionEditDialogComponent, {
      data: new ProductionVersion(),
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: ProductionVersion | null) => (result !== null) ),
      switchMap((result:ProductionVersion) => {
        this.dialogService.showProgress();
        result.requestId = request.id;
        return this.productionService.updateProductionVersion(result);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => { 
        this.dialogService.openInfoDialog(r.message);
        
        if( this.isRequestExpanded( request ) && this.versionGrid !== undefined ) {
          this.versionGrid.onRefresh();
        }

      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private isRequestExpanded(request: ProductionRequest): boolean
  {
    return this.expandedProductionRequest?.id === request.id;
  }

  onExpandRequest(request: ProductionRequest): void
  {
    this.expandedProductionRequest = this.isRequestExpanded(request) ? null : request;
  }

  onViewHistory(request: ProductionRequest): void
  {
    this.dialogService.openHistoryDialog(
      ProductionRequest.OBJECT_TYPE,
      request.id,
      `${request.client} - ${request.name}`,
      request.status,
      this.allowEditHistory
    );
  }

  allowDeleteRequest(request: ProductionRequest): boolean
  {
    return this.allowEditRequest && (request.status.id == Status.PRODUCTION_REQUEST_CREATED);
  }

  onDeleteRequest(request: ProductionRequest)
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete production request "${this.formatRequestId(request.requestId)}"?`,
      {
        title: 'Delete Production Request',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        return this.productionService.deleteProductionRequest(request.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  allowLinkMasterCommercial(request: ProductionRequest): boolean
  {
    return this.allowEditRequest && (request.status.id == Status.PRODUCTION_REQUEST_CREATED);
  }

  onLinkMasterCommercial(request: ProductionRequest)
  {
    const dialogRef = this.dialogService.openAnyDialog(LinkMasterCommercialDialogComponent, {
      width: '1100px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Commercial | null) => result != null),
      switchMap((result: Commercial) => {
        this.dialogService.showProgress();
        return this.productionService.updateProductionRequestMasterCommercial(request.id, result.id, result.referenceCode!);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
        this.onRefresh();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onViewSamples(request: ProductionRequest)
  {
    const dialogRef = this.dialogService.openAnyDialog(ProductionRequestSampleDialogComponent, {
      data: request,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((samples: any | null) => samples != null),
      switchMap((samples: any) => {
        this.dialogService.showProgress();
        return this.productionService.updateProductionRequestSampleStatus(request.id, samples);
      }),
      finalize(() => {
        this.productionService.getProductionRequests(this.filter).subscribe({
          next: (r:RESTApplicationResponse) => {
            this.dataSource.data = r.data;
          }
        });
      })
    ).subscribe({
      next: (r:any) => {
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onCopyToClipboard(data: string)
  {
    this._snackBar.open( `${data} copied to clipboard`, '',{duration: 2000});
  }

  formatRequestId(requestId: number): string
  {
    return 'OR' + requestId.toString().padStart(6, '0');
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'requestId': return item.id || 0;
        case 'client': return item.client?.toLowerCase() || '';
        case 'name': return item.name?.toLowerCase() || '';
        case 'origin': return item.origin?.description?.toLowerCase() || '';
        case 'airDate': return item.airDate;
        case 'masterRefCode': return item.masterRefCode?.toLowerCase() || '';
        case 'masterBrand': return item.masterBrand?.toLowerCase() || '';
        case 'masterProduct': return item.masterProduct?.toLowerCase() || '';
        default: return item[property];
      }
    };
  }

  private initPrivileges(): void
  {
    let decodedToken = this.authService.decodeToken(this.stateService.data.userToken);
    this.allowEditHistory = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.PROD_EDITHISTORY]);
    this.allowEditRequest = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.PROD_UPDATEORDER]);
  }
}
